import { BaseType, NumberValue, format, type Selection } from "d3";
import {
  annotationSvgGridline,
  axisBottom,
  axisLeft,
  axisRight,
  axisTop,
} from "d3fc";

import { doNothing } from "@examspark/common";

import { D3Axis, ticksForStep } from "../d3Utils";

const drawGrid = ({ x, y }: { x: D3Axis; y: D3Axis }) => {
  const grid = annotationSvgGridline()
    .xScale(x.scale)
    .yScale(y.scale)
    .xDecorate((selection: Selection<SVGElement, unknown, null, undefined>) => {
      selection.attr("stroke", "#ddd");
    })
    .yDecorate((selection: Selection<SVGElement, unknown, null, undefined>) => {
      selection.attr("stroke", "#ddd");
    });

  if (x.gridDistance) {
    grid.xTickValues(ticksForStep(x, x.gridDistance));
  }
  if (y.gridDistance) {
    grid.yTickValues(ticksForStep(y, y.gridDistance));
  }

  return grid;
};

const drawAxis = ({
  orientation,
  axis,
}: {
  orientation: "left" | "bottom" | "right" | "top";
  axis: D3Axis;
}) => {
  const fcAxis = getAxisFromOrientation(orientation)(axis.scale);

  if (axis.tickDistance) {
    fcAxis.tickValues(ticksForStep(axis, axis.tickDistance));
    if (axis.precision != null && axis.precision >= 0) {
      fcAxis.tickFormat((domainValue: NumberValue) => {
        let stringified = format(`.${axis.precision}f`)(+domainValue);
        if (axis.tickLabel) {
          stringified += axis.tickLabel.substring(0, 3);
        }
        return stringified;
      });
    }
  }

  return fcAxis;
};

const getAxisFromOrientation = (orientation: string) => {
  switch (orientation) {
    case "left":
      return axisLeft;
    case "right":
      return axisRight;
    case "top":
      return axisTop;
    case "bottom":
      return axisBottom;
  }
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const useGraphDecorator = <GType extends BaseType>(props: {
  x: D3Axis;
  y: D3Axis;
  margin: { top: number; right: number; bottom: number; left: number };
}) => {
  const width = props.x.scale.range().at(-1);
  const height = props.y.scale.range().at(-1);

  if (!width || !height) {
    console.error("invalid scales");
    return doNothing;
  }

  return (svg: Selection<GType, unknown, null, undefined>) => {
    const graph = svg
      .selectAll("g.graph")
      .data([props])
      .join("g")
      .attr("class", "graph")
      .attr(
        "transform",
        "translate(" + props.margin.left + "," + props.margin.top + ")",
      );

    const xAxisOffset = clamp(props.x.scale(0), 0, width);
    const yAxisOffset = clamp(props.y.scale(0), 0, height);

    const gridDecorator = drawGrid(props);

    const grid = graph
      .selectAll("g.gridline")
      .data([props])
      .join("g")
      .attr("class", "gridline")
      .call(gridDecorator);

    // X Axis
    const xAxisDecorator = drawAxis({
      orientation: yAxisOffset >= height / 2 ? "bottom" : "top",
      axis: props.x,
    });
    // Y Axis
    const yAxisDecorator = drawAxis({
      orientation: xAxisOffset > width / 2 ? "right" : "left",
      axis: props.y,
    });

    const xAxis = graph
      .selectAll("g.x-axis")
      .data([props])
      .join("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${yAxisOffset})`)
      .call(xAxisDecorator);

    const yAxis = graph
      .selectAll("g.y-axis")
      .data([props])
      .join("g")
      .attr("class", "y-axis")
      .call(yAxisDecorator)
      .attr("transform", `translate(${xAxisOffset}, 0)`);

    // all horz/vert lines look better as crisp edges
    // on lo-dpi displays
    [grid, xAxis, yAxis].forEach((g) =>
      g.attr("shape-rendering", "crispedges"),
    );

    // draw arrowheads
    xAxis
      .select(".domain")
      // the default axis line has these outer tick marks
      // that force our arrow to point the wrong way,
      // so draw a new (simpler) path
      .attr("d", "M0,0H" + width)
      .style("marker-end", props.x.max > 0 ? "url(#arrow-with-tail)" : "")
      .style("marker-start", props.x.min < 0 ? "url(#arrow-with-tail)" : "");

    yAxis
      .select(".domain")
      .attr("d", "M0,0V" + height)
      .style("marker-end", props.y.min < 0 ? "url(#arrow-with-tail)" : "")
      .style("marker-start", props.y.max > 0 ? "url(#arrow-with-tail)" : "");

    // Axis Labels
    graph
      .selectAll("text.x-axis-label")
      // an empty array will remove the label, hence the filter
      .data([props.x.label].filter(Boolean))
      .join("text")
      .attr("class", "x-axis-label")
      .attr("text-anchor", "beginning")
      .attr("dx", "1em")
      .attr("x", width)
      .attr("y", yAxisOffset)
      .text((d) => d ?? "");

    graph
      .selectAll("text.y-axis-label")
      .data([props.y.label].filter(Boolean))
      .join("text")
      .attr("class", "y-axis-label")
      .attr("text-anchor", "middle")
      .attr("dy", "-1em")
      .attr("y", 0)
      .attr("x", xAxisOffset)
      .text((d) => d ?? "");
  };
};
